<template>
  <div>
    <div class="row">
      <div class="col-12 mt-3">
        <Card body-classes="p-3" card-classes="shadow-sm">
          <template #header>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <h5 class="mb-0 text-primary">
                <vue-feather type="clipboard" class="me-2"></vue-feather>
                Stock Take Analytics
              </h5>
              <div class="d-flex align-items-center gap-3">
                <div class="d-flex align-items-center">
                  <label for="storeSelect" class="me-2 text-muted">Store:</label>
                  <select id="storeSelect" class="form-select form-select-sm w-auto" v-model="selectedStore">
                    <option value="">All Stores</option>
                    <option v-for="store in stores" :key="store.id" :value="store.id">{{ store.name }}</option>
                  </select>
                </div>
                <div class="d-flex align-items-center">
                  <label for="stockTakeSelect" class="me-2 text-muted">Stock Take:</label>
                  <select id="stockTakeSelect" class="form-select form-select-sm w-auto" v-model="selectedStockTake">
                    <option value="">All Stock Takes</option>
                    <option v-for="stockTake in filteredStockTakes" :key="stockTake.id" :value="stockTake.id">{{ stockTake.code }}</option>
                  </select>
                </div>
                <div class="d-flex align-items-center">
                  <label for="yearSelect" class="me-2 text-muted">Year:</label>
                  <select id="yearSelect" class="form-select form-select-sm w-auto" v-model="selectedYear" :disabled="!!selectedStockTake">
                    <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                  </select>
                </div>
              </div>
            </div>
          </template>
          <div>
            <div v-if="loading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading stock take analytics...</p>
            </div>
            <div v-else>
              <div class="row g-3 mb-4">
                <div class="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                  <div class="card h-100 border-0 shadow-sm">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="rounded-circle p-3 me-3 bg-primary-light">
                          <vue-feather type="clipboard" stroke="darkviolet" size="24"></vue-feather>
                        </div>
                        <div>
                          <h6 class="mb-0 text-muted">Total Stock Takes</h6>
                          <h3 class="mt-2 text-primary">{{ analytics.summary.total_stock_takes }}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                  <div class="card h-100 border-0 shadow-sm">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="rounded-circle p-3 me-3 bg-success-light">
                          <vue-feather type="check-circle" stroke="green" size="24"></vue-feather>
                        </div>
                        <div>
                          <h6 class="mb-0 text-muted">Completed</h6>
                          <h3 class="mt-2 text-success">{{ analytics.summary.completed }}</h3>
                          <small class="text-muted">{{ analytics.summary.completion_rate.toFixed(1) }}% completion rate</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                  <div class="card h-100 border-0 shadow-sm">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="rounded-circle p-3 me-3 bg-warning-light">
                          <vue-feather type="clock" stroke="orange" size="24"></vue-feather>
                        </div>
                        <div>
                          <h6 class="mb-0 text-muted">Pending</h6>
                          <h3 class="mt-2 text-warning">{{ analytics.summary.pending }}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                  <div class="card h-100 border-0 shadow-sm">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="rounded-circle p-3 me-3 bg-info-light">
                          <vue-feather type="target" stroke="dodgerblue" size="24"></vue-feather>
                        </div>
                        <div>
                          <h6 class="mb-0 text-muted">Accuracy Rate</h6>
                          <h3 class="mt-2 text-info">{{ analytics.accuracy.accuracy_rate.toFixed(1) }}%</h3>
                          <small class="text-muted">{{ analytics.accuracy.products_with_discrepancies }} discrepancies</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row g-4 mb-4">
                <div class="col-xl-4 col-lg-6">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-body">
                      <h6 class="text-muted mb-3">
                        <vue-feather type="package" size="18" class="me-2"></vue-feather>
                        Quantity Summary
                      </h6>
                      <div class="mb-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Expected</span>
                          <span class="fw-bold">{{ analytics.quantities.expected }} pcs</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Available</span>
                          <span class="fw-bold">{{ analytics.quantities.available }} pcs</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Difference</span>
                          <span :class="getDifferenceClass(analytics.quantities.difference)" class="fw-bold">
                            {{ getDifferenceSign(analytics.quantities.difference) }}{{ Math.abs(analytics.quantities.difference) }} pcs
                          </span>
                        </div>
                      </div>
                      <div class="alert alert-light mb-0">
                        <small class="text-muted">
                          <strong>{{ getDifferenceLabel(analytics.quantities.difference) }}</strong>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-xl-4 col-lg-6">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-body">
                      <h6 class="text-muted mb-3">
                        <vue-feather type="dollar-sign" size="18" class="me-2"></vue-feather>
                        Value Summary
                      </h6>
                      <div class="mb-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Expected Sale Value</span>
                          <span class="fw-bold">{{ formatNumber(analytics.values.expected_sell_value) }}</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Available Sale Value</span>
                          <span class="fw-bold">{{ formatNumber(analytics.values.available_sell_value) }}</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center">
                          <span class="text-muted">Value Difference</span>
                          <span :class="getDifferenceClass(analytics.values.sell_value_difference)" class="fw-bold">
                            {{ getDifferenceSign(analytics.values.sell_value_difference) }}{{ formatNumber(Math.abs(analytics.values.sell_value_difference)) }}
                          </span>
                        </div>
                      </div>
                      <div class="alert alert-light mb-0">
                        <small class="text-muted">
                          <strong>Financial Impact:</strong> {{ getValueImpactLabel(analytics.values.sell_value_difference) }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-xl-4 col-lg-12">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-body">
                      <h6 class="text-muted mb-3">
                        <vue-feather type="activity" size="18" class="me-2"></vue-feather>
                        Accuracy Metrics
                      </h6>
                      <div class="mb-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Total Products</span>
                          <span class="fw-bold">{{ analytics.accuracy.total_products }}</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Products with Discrepancies</span>
                          <span class="fw-bold text-danger">{{ analytics.accuracy.products_with_discrepancies }}</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Products with Shortage</span>
                          <span class="fw-bold text-warning">{{ analytics.accuracy.products_with_shortage }}</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="text-muted">Products with Overage</span>
                          <span class="fw-bold text-info">{{ analytics.accuracy.products_with_overage }}</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center">
                          <span class="text-muted">Accuracy Rate</span>
                          <span class="fw-bold text-success">{{ analytics.accuracy.accuracy_rate.toFixed(1) }}%</span>
                        </div>
                      </div>
                      <div class="progress" style="height: 20px;">
                        <div class="progress-bar bg-success" role="progressbar"
                             :style="{width: analytics.accuracy.accuracy_rate + '%'}"
                             :aria-valuenow="analytics.accuracy.accuracy_rate"
                             aria-valuemin="0" aria-valuemax="100">
                          {{ analytics.accuracy.accuracy_rate.toFixed(1) }}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row" v-if="analytics.chart.labels.length > 0">
                <div class="col-12">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body">
                      <h6 class="text-muted mb-3">Monthly Variance Value ({{ selectedYear }})</h6>
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <span class="text-primary fw-bold">
                          Total Variance: {{ formatNumber(getTotalVariance()) }}
                        </span>
                        <span class="badge bg-warning-light text-warning">
                          {{ analytics.chart.labels.length }} Months
                        </span>
                      </div>
                      <div>
                        <LineChart
                            :data="[analytics.chart.datasets[0]]"
                            :labels="analytics.chart.labels"
                        ></LineChart>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="alert alert-info">
                <vue-feather type="info" size="18" class="me-2"></vue-feather>
                No monthly chart data available for the selected filters.
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import {decrypt} from "@/utils/crypto";
import analyticsService from "@/api/analyticsService";
import storeService from "@/api/storeService";
import formatNumber from "@/utils/formatNumber";
import LineChart from "@/components/charts/LineChart.vue";

export default {
  name: "StockTakeAnalytics",
  components: {LineChart},

  data(){
    return {
      profile: {},
      loading: true,
      stores: [],
      stockTakes: {},
      selectedStore: '',
      selectedStockTake: '',
      selectedYear: new Date().getFullYear(),
      years: [],
      analytics: {
        summary: {
          total_stock_takes: 0,
          completed: 0,
          pending: 0,
          completion_rate: 0
        },
        quantities: {
          expected: 0,
          available: 0,
          difference: 0,
          variance: 0
        },
        values: {
          expected_sell_value: 0,
          available_sell_value: 0,
          sell_value_difference: 0
        },
        accuracy: {
          total_products: 0,
          products_with_discrepancies: 0,
          products_with_shortage: 0,
          products_with_overage: 0,
          accuracy_rate: 0
        },
        chart: {
          labels: [],
          datasets: [
            {
              label: 'Monthly Variance Value',
              data: []
            }
          ]
        }
      },
    }
  },

  methods: {
    formatNumber,
    async getAnalytics() {
      this.loading = true
      try {
        const params = {};

        // When stock_take_id is provided, year and store_id are ignored
        if (this.selectedStockTake) {
          params.stock_take_id = this.selectedStockTake;
          this.analytics = await analyticsService.getStockTakeAnalytics(null, params);
        } else {
          if (this.selectedStore) {
            params.store_id = this.selectedStore;
          }
          this.analytics = await analyticsService.getStockTakeAnalytics(this.selectedYear, params);
        }

        // Update stockTakes and years lists from response
        // stock_takes is now grouped by store_id: { store_id: [stock_takes] }
        if (this.analytics.stock_takes) {
          this.stockTakes = this.analytics.stock_takes;
        }
        if (this.analytics.years) {
          this.years = this.analytics.years;
        }
      } catch(e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadDropdowns() {
      try {
        this.stores = await storeService.getStores();
      } catch(e) {
        console.error(e);
      }
    },
    getDifferenceClass(value) {
      if (value > 0) return 'text-danger';
      if (value < 0) return 'text-success';
      return 'text-muted';
    },
    getDifferenceSign(value) {
      if (value > 0) return '-';
      if (value < 0) return '+';
      return '';
    },
    getDifferenceLabel(value) {
      if (value > 0) return 'Stock Shortage - Expected more than found';
      if (value < 0) return 'Stock Overage - Found more than expected';
      return 'Perfect Match';
    },
    getValueImpactLabel(value) {
      if (value > 0) return 'Potential revenue loss from stock shortage';
      if (value < 0) return 'Potential revenue gain from stock overage';
      return 'No financial impact';
    },
    getTotalVariance() {
      if (!this.analytics.chart.datasets[0] || !this.analytics.chart.datasets[0].data) {
        return 0;
      }
      return this.analytics.chart.datasets[0].data.reduce((sum, val) => sum + val, 0);
    }
  },

  computed: {
    filteredStockTakes() {
      if (!this.selectedStore) {
        // Return all stock takes from all stores as a flat array
        return Object.values(this.stockTakes).flat();
      }
      // Return stock takes for the selected store
      return this.stockTakes[this.selectedStore] || [];
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    await this.loadDropdowns();
    await this.getAnalytics();
  },

  watch: {
    selectedYear: async function () {
      await this.getAnalytics();
    },
    selectedStore: async function () {
      // Reset selectedStockTake if it doesn't belong to the newly selected store
      if (this.selectedStockTake && this.selectedStore) {
        const storeStockTakes = this.stockTakes[this.selectedStore] || [];
        const exists = storeStockTakes.some(st => st.id === this.selectedStockTake);
        if (!exists) {
          this.selectedStockTake = '';
        }
      }
      await this.getAnalytics();
    },
    selectedStockTake: async function () {
      await this.getAnalytics();
    }
  }
}
</script>

<style scoped>
.bg-primary-light { background-color: rgba(99, 102, 241, 0.1); }
.bg-success-light { background-color: rgba(34, 197, 94, 0.1); }
.bg-warning-light { background-color: rgba(251, 146, 60, 0.1); }
.bg-info-light { background-color: rgba(59, 130, 246, 0.1); }
</style>
